import { useEffect, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { Card, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import apiClient from '@/api/client'

interface ClinicCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  questions_count: number
  order: number
}

export default function AdminClinicPage() {
  const [categories, setCategories] = useState<ClinicCategory[]>([])
  const [loading, setLoading] = useState(true)

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ClinicCategory | null>(null)
  const [form, setForm] = useState({ name: '', description: '', icon: 'question-mark-circle', order: 0 })
  const [saving, setSaving] = useState(false)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<ClinicCategory | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/clinic-categories/')
      setCategories(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenCreate = () => {
    setEditingCategory(null)
    setForm({ name: '', description: '', icon: 'question-mark-circle', order: categories.length + 1 })
    setShowModal(true)
  }

  const handleOpenEdit = (category: ClinicCategory) => {
    setEditingCategory(category)
    setForm({
      name: category.name,
      description: category.description,
      icon: category.icon,
      order: category.order,
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (editingCategory) {
        await apiClient.patch(`/admin/clinic-categories/${editingCategory.slug}/`, form)
      } else {
        await apiClient.post('/admin/clinic-categories/', form)
      }
      setShowModal(false)
      fetchCategories()
    } catch (error) {
      console.error('Error saving category:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!categoryToDelete) return
    setDeleting(true)
    try {
      await apiClient.delete(`/admin/clinic-categories/${categoryToDelete.slug}/`)
      setShowDeleteModal(false)
      setCategoryToDelete(null)
      fetchCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento categorie..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Compliance Clinic</h1>
          <p className="text-gray-600">Gestisci le categorie delle domande</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova Categoria
        </Button>
      </div>

      {/* Categories Table */}
      {categories.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordine</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domande</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {category.order}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <QuestionMarkCircleIcon className="h-5 w-5 text-primary-600" />
                        <span className="font-medium text-gray-900">{category.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-md truncate">
                      {category.description}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {category.questions_count}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(category)}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                          title="Modifica"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => { setCategoryToDelete(category); setShowDeleteModal(true); }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Elimina"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<QuestionMarkCircleIcon className="h-12 w-12" />}
            title="Nessuna categoria"
            description="Crea la prima categoria per le domande"
            action={{
              label: 'Nuova Categoria',
              onClick: handleOpenCreate
            }}
          />
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                min={0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icona</label>
              <select
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="question-mark-circle">Domanda</option>
                <option value="scale">Bilancia</option>
                <option value="shield">Scudo</option>
                <option value="document-search">Ricerca</option>
                <option value="building-office">Edificio</option>
                <option value="exclamation-triangle">Attenzione</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModal(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Categoria">
        <ModalBody>
          {categoryToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare la categoria{' '}
                <span className="font-semibold">{categoryToDelete.name}</span>?
              </p>
              {categoryToDelete.questions_count > 0 && (
                <p className="text-sm text-amber-600">
                  Attenzione: questa categoria contiene {categoryToDelete.questions_count} domande.
                </p>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
